import { logDeeplinkEvent } from '@p2p/pages/Redirect/deeplink/deepLinkLogger';
import { TGetOrderResponse } from '@p2p/services';
import { copyToClipboard } from '@p2p/shared/functions';
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { getQuickPayCopyValue, QuickPayBank, QuickPayLink } from './quickPay.banks';
import {
  getQuickPayPlatform,
  openAppLink,
  openWebLinkInNewTab,
  QUICK_PAY_ANDROID_FALLBACK_DELAY_MS,
  QUICK_PAY_APP_OPEN_TIMEOUT_MS,
  QUICK_PAY_SCHEME_INTERVAL_MS,
  QUICK_PAY_SUSPEND_MARGIN_MS,
  QUICK_PAY_WEB_FALLBACK_DELAY_MS,
} from './quickPay.utils';

const isWebLink = (link: string) => /^https?:/i.test(link);

type LaunchStatus = 'success' | 'fail';

/**
 * P2P-3038/P2P-3039: запуск приложения банка (Газпромбанк, Озон Банк) с кнопки
 * «Оплатить в один клик».
 *
 * Перед переходом копируем реквизит в буфер обмена (телефон получателя, по карте - её
 * номер). Android: основная схема, через 50 мс - фоллбек intent://. Ушла страница в фон -
 * приложение открылось: ставим ?status=success, и по возвращении плательщик видит окно
 * «Вы оплатили?» (SuccessModal, как у остальных диплинков): «Да» - дальше как по
 * «Я оплатил», «Нет» - остаётся на реквизитах и может нажать кнопку ещё раз. Через 1800 мс
 * страница всё ещё на экране - попытка неудачная: ?status=fail, кнопка скрывается,
 * остаются обычные реквизиты.
 *
 * P2P-3088, десктоп: вместо приложения открываем веб-версию банка в новой вкладке. Проверки
 * «не открылось» там нет (на десктопе её не определить), поэтому ?status=fail не ставится
 * и кнопка не скрывается; окно «Вы оплатили?» показывается при возврате на вкладку.
 *
 * P2P-3112, очередь переходов на телефоне (Альфа-Банк): ссылки открываются по порядку через
 * 50 мс, пока страница не уйдёт в фон; перед веб-версией банка в конце очереди пауза дольше.
 * Проверка «не открылось» - через 1800 мс после последнего перехода.
 */
export const useQuickPay = (order: TGetOrderResponse, bank: QuickPayBank, link: QuickPayLink) => {
  const [, setSearchParams] = useSearchParams();
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => cleanupRef.current?.(), []);

  const log = useCallback(
    (event: 'deeplink_btn_click' | 'deeplink_success' | 'deeplink_failed', scheme?: string) => {
      logDeeplinkEvent(event, {
        order_id: order.id,
        bank: bank.code,
        platform: getQuickPayPlatform(),
        fromQRCode: false,
        scheme,
      });
    },
    [order.id, bank.code],
  );

  const launch = useCallback(() => {
    cleanupRef.current?.();

    const platform = getQuickPayPlatform();
    const isDesktop = platform === 'desktop';
    const mobileQueue = isDesktop ? undefined : link.mobileQueue?.[platform];
    let target = isDesktop ? link.webUrl : link.appLink;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const visibilityListener = new AbortController();
    let finished = false;

    const cleanup = () => {
      visibilityListener.abort();
      timers.forEach(clearTimeout);
      cleanupRef.current = null;
    };

    const finish = (status: LaunchStatus) => {
      if (finished) {
        return;
      }
      finished = true;
      cleanup();
      log(status === 'success' ? 'deeplink_success' : 'deeplink_failed', target);
      setSearchParams(
        (params) => {
          params.set('status', status);

          return params;
        },
        { replace: true },
      );
    };

    cleanupRef.current = cleanup;
    log('deeplink_btn_click');

    const copyValue = getQuickPayCopyValue(order);

    if (copyValue) {
      void copyToClipboard(copyValue);
    }

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          finish('success');
        }
      },
      { signal: visibilityListener.signal },
    );
    // Переход на сайт банка в той же вкладке: страница выгружается.
    window.addEventListener('pagehide', () => finish('success'), {
      signal: visibilityListener.signal,
    });

    const scheduleFailCheck = () => {
      const checkStartedAt = Date.now();

      timers.push(
        setTimeout(() => {
          const wasSuspended =
            Date.now() - checkStartedAt >
            QUICK_PAY_APP_OPEN_TIMEOUT_MS + QUICK_PAY_SUSPEND_MARGIN_MS;

          finish(document.hidden || wasSuspended ? 'success' : 'fail');
        }, QUICK_PAY_APP_OPEN_TIMEOUT_MS),
      );
    };

    if (isDesktop) {
      // Веб-версия банка в новой вкладке: страница с реквизитами и таймером остаётся
      // открытой, а по возвращении на неё сработает visibilitychange и окно «Вы оплатили?».
      if (link.webUrl) {
        openWebLinkInNewTab(link.webUrl);
      }

      return;
    }

    if (mobileQueue?.length) {
      const openAt = (index: number) => {
        if (finished) {
          return;
        }
        // Страница уже в фоне (событие могло не дойти) - приложение открылось на прошлой ссылке.
        if (document.hidden) {
          finish('success');

          return;
        }

        target = mobileQueue[index];
        openAppLink(target);

        const next = mobileQueue[index + 1];

        if (next === undefined) {
          scheduleFailCheck();

          return;
        }

        timers.push(
          setTimeout(
            () => openAt(index + 1),
            isWebLink(next) ? QUICK_PAY_WEB_FALLBACK_DELAY_MS : QUICK_PAY_SCHEME_INTERVAL_MS,
          ),
        );
      };

      openAt(0);

      return;
    }

    openAppLink(link.appLink);

    const { intentLink } = link;

    if (platform === 'android' && intentLink) {
      timers.push(
        setTimeout(() => {
          if (!finished && !document.hidden) {
            openAppLink(intentLink);
          }
        }, QUICK_PAY_ANDROID_FALLBACK_DELAY_MS),
      );
    }

    scheduleFailCheck();
  }, [link, log, order, setSearchParams]);

  return { launch };
};
